import { useCallback, useMemo } from 'react';
import { resolveMainSession } from '@citizen-walk/shared-types';
import { useCurrentSession, type CurrentSessionState, type SessionItem } from './useCurrentSession';
import { useMembershipSessions } from './useMembershipSessions';

/**
 * 投稿に使う「メインセッション」を解決する統合 hook (案1 統合、2026-06-16)。
 *
 * 旧 v2 は localStorage `cw.currentSessionId` を投稿/フィルタ/URL/お知らせの「現セッション」にしていたが、
 * これは端末ローカルで参加セッション (joinedSessionIds) と非連動だった。本 hook は
 * `membership.mainSessionId` (ユーザー単位・端末間同期) を唯一の供給源に統一する:
 *   - `state.currentSessionId` = 有効メインセッション (resolveMainSession で解決)
 *     ・参加 1 つ → 自動 / 参加複数 → mainSessionId or 表示順先頭 / 参加 0 → null。
 *   - `setCurrentSessionId(id)` = membership.mainSessionId を更新 (id を参加扱いにしてから main 化)。
 *   - `joinedActiveSessions` = 参加かつ active なセッション (表示順)。ドロップダウンの選択肢。
 *   - `needsMainSelection` = 参加が 2 つ以上 → 投稿画面トップ / 設定でメイン選択 UI を出す。
 *
 * useCurrentSession (セッション一覧 items の購読) はそのまま流用し、現セッションの供給だけ membership に
 * 切替える (AnalysisPage / SettingsModal / Onboarding は items のみ使うため従来 hook を継続使用)。
 */
export interface UseMainSessionReturn {
  state: CurrentSessionState;
  setCurrentSessionId: (id: string | null) => void;
  joinedActiveSessions: SessionItem[];
  needsMainSelection: boolean;
}

const EMPTY_ITEMS: SessionItem[] = [];

export function useMainSession(
  uid: string | null,
  districtId: string | null,
): UseMainSessionReturn {
  const { state: sessionState } = useCurrentSession(districtId);
  const {
    joinedSessionIds,
    mainSessionId,
    loaded: membershipLoaded,
    setMainSessionId,
  } = useMembershipSessions(uid, districtId);

  const items = useMemo(
    () => (sessionState.status === 'ready' ? sessionState.items : EMPTY_ITEMS),
    [sessionState],
  );

  const effectiveMain = useMemo(
    () =>
      resolveMainSession(
        joinedSessionIds,
        mainSessionId,
        items.map((s) => s.id),
      ),
    [joinedSessionIds, mainSessionId, items],
  );

  const joinedActiveSessions = useMemo(() => {
    const joined = new Set(joinedSessionIds);
    return items.filter((s) => joined.has(s.id));
  }, [items, joinedSessionIds]);

  const needsMainSelection = joinedActiveSessions.length >= 2;

  // membership 読込中は 'loading' を維持し、投稿画面の「セッション未選択」バナーの誤点滅を防ぐ。
  const state: CurrentSessionState = useMemo(() => {
    if (sessionState.status === 'error') {
      return {
        status: 'error',
        items: [],
        currentSessionId: effectiveMain,
        error: sessionState.error,
      };
    }
    if (sessionState.status === 'loading' || !membershipLoaded) {
      return { status: 'loading', items: [], currentSessionId: effectiveMain, error: null };
    }
    return {
      status: 'ready',
      items: sessionState.items,
      currentSessionId: effectiveMain,
      error: null,
    };
  }, [sessionState, membershipLoaded, effectiveMain]);

  const setCurrentSessionId = useCallback(
    (id: string | null) => {
      void setMainSessionId(id);
    },
    [setMainSessionId],
  );

  return { state, setCurrentSessionId, joinedActiveSessions, needsMainSelection };
}
